import _objectSpread from "@babel/runtime/helpers/objectSpread2";
/** Parse storyboard as AST. */
export function parseStoryboard(storyboard) {
  var _storyboard$meta;
  return {
    type: "Root",
    raw: storyboard,
    routes: parseRoutes(storyboard.routes),
    templates: parseTemplates((_storyboard$meta = storyboard.meta) === null || _storyboard$meta === void 0 ? void 0 : _storyboard$meta.customTemplates)
  };
}
/** Parse storyboard routes as AST. */
export function parseRoutes(routes, options) {
  if (Array.isArray(routes)) {
    return routes.map(route => _objectSpread(_objectSpread({
      type: "Route",
      raw: route
    }, options !== null && options !== void 0 && options.routesOnly ? null : {
      context: parseContext(route.context),
      redirect: parseResolvable(route.redirect),
      menu: parseMenu(route.menu),
      providers: parseRouteProviders(route.providers),
      defineResolves: Array.isArray(route.defineResolves) ? route.defineResolves.map(item => parseResolvable(item)).filter(Boolean) : undefined
    }), {}, {
      children: route.type === "routes" ? parseRoutes(route.routes, options) : parseBricks(route.bricks, options)
    }));
  }
  return [];
}

/** Parse storyboard templates as AST. */
export function parseTemplates(templates) {
  if (Array.isArray(templates)) {
    return templates.map(parseTemplate);
  }
  return [];
}

/** Parse a storyboard template as AST. */
export function parseTemplate(tpl) {
  return {
    type: "Template",
    raw: tpl,
    bricks: parseBricks(tpl.bricks),
    context: parseContext(tpl.state)
  };
}
function parseBricks(bricks, options) {
  if (Array.isArray(bricks)) {
    return bricks.map(brick => parseBrick(brick, options));
  }
  return [];
}

/** Parse a storyboard brick as AST. */
export function parseBrick(brick, options) {
  return _objectSpread(_objectSpread({
    type: "Brick",
    raw: brick,
    isUseBrick: options === null || options === void 0 ? void 0 : options.isUseBrick
  }, options !== null && options !== void 0 && options.routesOnly ? null : _objectSpread(_objectSpread({
    if: parseCondition(brick.if),
    events: parseEvents(brick.events),
    lifeCycle: parseLifeCycles(brick.lifeCycle)
  }, parseBrickProperties(brick.properties)), {}, {
    context: parseContext(brick.context)
  })), {}, {
    children: parseSlots(brick.slots, options)
  });
}
function parseCondition(condition) {
  if (isObject(condition)) {
    return {
      type: "ResolvableCondition",
      resolve: parseResolvable(condition)
    };
  }
  return {
    type: "LiteralCondition"
  };
}
function parseBrickProperties(props) {
  var useBrick = [];
  var useBackend = [];
  function walkBrickProperties(value) {
    if (Array.isArray(value)) {
      for (var item of value) {
        walkBrickProperties(item);
      }
    } else if (isObject(value)) {
      if (value.useBrick || value.useBackend) {
        var _value$useBackend;
        if (value.useBrick) {
          useBrick.push({
            type: "UseBrickEntry",
            rawContainer: value,
            rawKey: "useBrick",
            children: parseBricks([].concat(value.useBrick), {
              isUseBrick: true
            })
          });
        }
        var provider = (_value$useBackend = value.useBackend) === null || _value$useBackend === void 0 ? void 0 : _value$useBackend.provider;
        if (typeof provider === "string") {
          useBackend.push({
            type: "UseBackendEntry",
            rawContainer: value,
            rawKey: "useBackend",
            children: [parseBrick({
              brick: provider
            })]
          });
        }
      } else {
        for (var _item of Object.values(value)) {
          walkBrickProperties(_item);
        }
      }
    }
  }
  walkBrickProperties(props);
  return {
    useBrick,
    useBackend
  };
}
function parseLifeCycles(lifeCycle) {
  if (isObject(lifeCycle)) {
    return Object.entries(lifeCycle).map(_ref => {
      var [name, conf] = _ref;
      switch (name) {
        case "useResolves":
          return {
            type: "ResolveLifeCycle",
            rawContainer: lifeCycle,
            rawKey: name,
            resolves: Array.isArray(conf) ? conf.map(item => parseResolvable(item, true)).filter(Boolean) : undefined
          };
        case "onPageLoad":
        case "onPageLeave":
        case "onAnchorLoad":
        case "onAnchorUnload":
        case "onMessageClose":
        case "onBeforePageLoad":
        case "onBeforePageLeave":
        case "onMount":
        case "onUnmount":
        case "onMediaChange":
          return {
            type: "SimpleLifeCycle",
            name,
            rawContainer: lifeCycle,
            rawKey: name,
            handlers: parseEventHandlers(conf)
          };
        case "onMessage":
        case "onScrollIntoView":
          return {
            type: "ConditionalLifeCycle",
            name,
            events: [].concat(conf).filter(Boolean).map(item => ({
              type: "ConditionalEvent",
              rawContainer: item,
              rawKey: "handlers",
              handlers: parseEventHandlers(item.handlers)
            }))
          };
        default:
          return {
            type: "UnknownLifeCycle"
          };
      }
    });
  }
}
function parseSlots(slots, options) {
  if (isObject(slots)) {
    return Object.entries(slots).map(_ref2 => {
      var [slot, conf] = _ref2;
      return {
        type: "Slot",
        raw: conf,
        slot,
        childrenType: conf.type === "routes" ? "Route" : "Brick",
        children: conf.type === "routes" ? parseRoutes(conf.routes, options) : parseBricks(conf.bricks, options)
      };
    });
  }
  return [];
}
function parseEvents(events) {
  if (isObject(events)) {
    return Object.entries(events).map(_ref3 => {
      var [eventType, handlers] = _ref3;
      return {
        type: "Event",
        rawContainer: events,
        rawKey: eventType,
        handlers: parseEventHandlers(handlers)
      };
    });
  }
}
function parseContext(contexts) {
  if (Array.isArray(contexts)) {
    return contexts.map(context => ({
      type: "Context",
      raw: context,
      resolve: parseResolvable(context.resolve),
      onChange: parseEventHandlers(context.onChange)
    }));
  }
}
function parseMenu(menu) {
  if (menu === false) {
    return {
      type: "FalseMenu"
    };
  }
  if (!menu) {
    return;
  }
  switch (menu.type) {
    case "brick":
      return {
        type: "BrickMenu",
        raw: menu,
        brick: parseBrick(menu)
      };
    case "resolve":
      return {
        type: "ResolvableMenu",
        resolve: parseResolvable(menu.resolve)
      };
    default:
      return {
        type: "StaticMenu"
      };
  }
}
function parseResolvable(resolve, isConditional) {
  if (isObject(resolve)) {
    return {
      type: "Resolvable",
      raw: resolve,
      isConditional
    };
  }
}
function parseEventHandlers(handlers) {
  return [].concat(handlers).filter(Boolean).map(handler => ({
    type: "EventHandler",
    callback: parseEventCallback(handler.callback),
    then: parseEventHandlers(handler.then),
    else: parseEventHandlers(handler.else),
    raw: handler
  }));
}
function parseEventCallback(callback) {
  if (isObject(callback)) {
    return Object.entries(callback).map(_ref4 => {
      var [callbackType, handlers] = _ref4;
      return {
        type: "EventCallback",
        rawContainer: callback,
        rawKey: callbackType,
        handlers: parseEventHandlers(handlers)
      };
    });
  }
}
function parseRouteProviders(providers) {
  if (Array.isArray(providers)) {
    return providers.map(provider => parseBrick(typeof provider === "string" ? {
      brick: provider
    } : provider));
  }
}

// Ref https://github.com/lodash/lodash/blob/4.17.11/lodash.js#L11744
function isObject(value) {
  var type = typeof value;
  return value != null && (type == "object" || type == "function");
}
//# sourceMappingURL=parseStoryboard.js.map